import React, { useEffect, useRef, useState } from "react";
import { Bot, Loader2, Trophy, AlertTriangle, Wrench, Sparkles, Square, Share2, Download, MessageSquarePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import confetti from "canvas-confetti";

const BOT_SECONDS = 40;
const SEV = {
  high: { cls: "text-rose-300 border-rose-500/40 bg-rose-500/10", label: "High" },
  medium: { cls: "text-amber-300 border-amber-500/40 bg-amber-500/10", label: "Medium" },
  low: { cls: "text-sky-300 border-sky-500/40 bg-sky-500/10", label: "Low" },
};

// Live Bot Playtester — sends an AI robot to actually PLAY the visible game, then shows a scored
// report with concrete findings. Owners can one-tap "Auto-fix with AI" (reuses the Live Creator
// sandbox→accept pipeline). Drives the runner via the GameCanvas ref; listens for gv-bot-report.
export default function AutoPlaytestButton({ canvasRef, gameId, canFix = false, autoRun = false, onFixed, onReport }) {
  const [phase, setPhase] = useState("idle"); // idle | running | done
  const [elapsed, setElapsed] = useState(0);
  const [report, setReport] = useState(null);
  const [open, setOpen] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [fixed, setFixed] = useState(false);
  const [scoreCard, setScoreCard] = useState(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [posted, setPosted] = useState(false);
  const activeRef = useRef(false);
  const tickRef = useRef(null);
  const autoRanRef = useRef(false);
  const maybeAutoRunRef = useRef(null);

  const clearTick = () => { if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; } };

  const celebrate = () => {
    try {
      const burst = (x) => confetti({ particleCount: 90, spread: 70, origin: { x, y: 0.6 }, colors: ["#7c3aed", "#3b9eff", "#34d399", "#fbbf24"] });
      burst(0.3); setTimeout(() => burst(0.7), 180);
    } catch {}
  };

  useEffect(() => {
    const handler = (ev) => {
      const d = ev.data || {};
      if (d.type === "ready") { if (maybeAutoRunRef.current) maybeAutoRunRef.current(); return; }
      if (d.type !== "gv-bot-report" || !activeRef.current) return;
      activeRef.current = false;
      clearTick();
      setReport(d.report || null);
      setPhase("done");
      setOpen(true);
      setScoreCard(null);
      setPosted(false);
      onReport?.(d.report || null);
      if (canFix && d.report) {
        api.post(`/creator/${gameId}/botreport`, d.report)
          .then((r) => { if (r.data?.newly_certified) { celebrate(); toast.success("🎉 Certified Fun! This game scored 80+ — it now gets a badge in the arcade.", { duration: 6000 }); } })
          .catch(() => {});
      }
    };
    window.addEventListener("message", handler);
    return () => { window.removeEventListener("message", handler); clearTick(); };
  }, [canFix, gameId, onReport]);

  const run = (silentToast = false) => {
    if (!canvasRef?.current?.botRun) { toast.error("Start playing first, then send the robot in."); return; }
    setReport(null); setFixed(false);
    activeRef.current = true;
    setPhase("running"); setElapsed(0);
    if (!silentToast) toast.message("🤖 Robot is playtesting your game…");
    clearTick();
    tickRef.current = setInterval(() => setElapsed((e) => {
      const n = e + 1;
      if (n >= BOT_SECONDS + 6 && activeRef.current) { // safety timeout if no report came back
        activeRef.current = false; clearTick(); setPhase("idle");
        toast.message("The robot finished but sent no report — try again while the game is running.");
      }
      return n;
    }), 1000);
    canvasRef.current.botRun(BOT_SECONDS);
  };

  const stop = () => { if (canvasRef?.current?.botStop) canvasRef.current.botStop(); };

  // Produce-Flow QA — auto-run the robot once when a freshly produced game is opened, so it
  // auto-flags play-to-win blockers before the creator starts tweaking. Fires on the iframe
  // 'ready' signal (reliable) with a long fallback poll; clears the server flag on start.
  maybeAutoRunRef.current = () => {
    if (!autoRun || autoRanRef.current || !canvasRef?.current?.botRun) return false;
    autoRanRef.current = true;
    toast.message("🤖 Auto-playtesting your produced game for blockers…");
    api.post(`/creator/${gameId}/robot-armed/clear`).catch(() => {});
    run(true);
    return true;
  };
  useEffect(() => {
    if (!autoRun) return;
    let tries = 0;
    const iv = setInterval(() => {
      if ((maybeAutoRunRef.current && maybeAutoRunRef.current()) || ++tries > 80) clearInterval(iv);
    }, 750);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRun]);

  const autoFix = async () => {
    const findings = (report?.findings || []).filter((f) => f.fix);
    if (!findings.length || fixing) return;
    setFixing(true);
    try {
      const { data } = await api.post(`/creator/${gameId}/bot-fix`, { findings }, { timeout: 90000 });
      if (!data.changed || !data.config) { toast.message(data.summary || "No safe change could be applied automatically."); return; }
      onFixed?.(data.config);
      setFixed(true);
      toast.success(`🤖 Applied ${findings.length} fix${findings.length === 1 ? "" : "es"}${data.version ? ` — saved v${data.version}` : ""}. Playtest again to see the new score!`);
    } catch (e) {
      const dt = e.response?.data?.detail;
      toast.error((typeof dt === "string" ? dt : formatApiError(dt)) || "Couldn't apply the fixes.");
    } finally { setFixing(false); }
  };

  const makeCard = async () => {
    if (cardLoading) return;
    setCardLoading(true);
    try {
      const { data } = await api.post(`/creator/${gameId}/score-card`, { verdict: report?.verdict || "" });
      const base = process.env.REACT_APP_BACKEND_URL || "";
      setScoreCard(`${base}${data.url}?t=${Date.now()}`);
    } catch (e) {
      const dt = e.response?.data?.detail;
      toast.error((typeof dt === "string" ? dt : formatApiError(dt)) || "Couldn't make the score card.");
    } finally { setCardLoading(false); }
  };

  const postToFeed = async () => {
    if (posting || posted) return;
    setPosting(true);
    try {
      await api.post(`/creator/${gameId}/score-card/share`, { verdict: report?.verdict || "" });
      setPosted(true);
      toast.success("Posted to your game's community feed! 🎉");
    } catch (e) {
      const dt = e.response?.data?.detail;
      toast.error((typeof dt === "string" ? dt : formatApiError(dt)) || "Couldn't post to the feed.");
    } finally { setPosting(false); }
  };

  const score = report?.score ?? 0;
  const scoreCls = score >= 75 ? "text-emerald-400" : score >= 45 ? "text-amber-400" : "text-rose-400";

  return (
    <>
      {phase === "running" ? (
        <div className="flex items-center gap-2" data-testid="bot-running-indicator">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/15 border border-violet-500/40 text-violet-300 text-xs font-semibold">
            <Bot className="w-3.5 h-3.5 animate-pulse" /> Robot playing… {Math.max(0, BOT_SECONDS - elapsed)}s
          </div>
          <Button variant="outline" size="sm" onClick={stop} className="rounded-full gap-1.5" data-testid="bot-stop-btn">
            <Square className="w-3.5 h-3.5 fill-current" /> Stop
          </Button>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={run}
          className="rounded-full gap-1.5 border-violet-500/40 text-violet-300 hover:bg-violet-500/10"
          data-testid="bot-playtest-btn">
          <Bot className="w-4 h-4" /> Robot playtest
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="bot-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Bot className="w-5 h-5 text-violet-400" /> Robot playtest report</DialogTitle>
            <DialogDescription>{report?.verdict}</DialogDescription>
          </DialogHeader>
          {report && (
            <>
              <div className="flex items-center justify-center gap-6 py-2">
                <div className="text-center">
                  <div className={`text-5xl font-black ${scoreCls}`} data-testid="bot-score">{score}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Playability</div>
                </div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] space-y-1">
                  <div className="flex items-center gap-1.5">{report.reachedGoal ? <Trophy className="w-3.5 h-3.5 text-emerald-400" /> : <span className="w-3.5 h-3.5 inline-block" />} {report.reachedGoal ? `Reached the goal${report.completeSeconds ? ` in ${report.completeSeconds}s` : ""}` : "Did not finish"}</div>
                  <div>🎯 Collected: <b>{report.collectiblesGained}</b>{report.missionsTotal ? <> · 🗺️ Levels: <b>{report.levelsCompleted}/{report.missionsTotal}</b></> : (report.levelsCompleted ? <> · 🗺️ Levels: <b>{report.levelsCompleted}</b></> : null)}</div>
                  <div>🏃 Distance: <b>{report.distance}</b> · stuck <b>{report.stuck}×</b></div>
                  {report.fpsAvg ? <div data-testid="bot-fps">🎞️ FPS: <b className={report.fpsAvg < 30 ? "text-amber-400" : ""}>{report.fpsAvg}</b> avg{report.fpsMin ? <> · <b>{report.fpsMin}</b> min</> : null}</div> : null}
                  {report.errors ? <div className="text-rose-400" data-testid="bot-errors">⚠️ Runtime errors: <b>{report.errors}</b></div> : null}
                  <div>⏱️ Played <b>{report.seconds}s</b></div>
                </div>
              </div>

              {(report.findings || []).length > 0 ? (
                <div className="space-y-2" data-testid="bot-findings">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">What the robot found</div>
                  {report.findings.map((f, i) => {
                    const sev = SEV[f.severity] || SEV.medium;
                    return (
                      <div key={i} className="rounded-xl border border-[hsl(var(--border))] p-2.5" data-testid={`bot-finding-${i}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${sev.cls}`}><AlertTriangle className="w-3 h-3" /> {sev.label}</span>
                          <span className="text-sm font-semibold">{f.title}</span>
                        </div>
                        {f.fix && <p className="text-xs text-[hsl(var(--muted-foreground))] flex items-start gap-1.5"><Wrench className="w-3.5 h-3.5 shrink-0 mt-0.5 text-violet-400" /> {f.fix}</p>}
                      </div>
                    );
                  })}
                  {canFix && !fixed && (
                    <Button onClick={autoFix} disabled={fixing} className="w-full rounded-full gap-1.5 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white" data-testid="bot-autofix-btn">
                      {fixing ? <><Loader2 className="w-4 h-4 animate-spin" /> Fixing with AI…</> : <><Sparkles className="w-4 h-4" /> Auto-fix all with AI</>}
                    </Button>
                  )}
                  {fixed && <p className="text-[12px] text-emerald-400 font-semibold text-center" data-testid="bot-fixed">✓ Fixes applied & saved — run the robot again to re-score!</p>}
                  {fixed && (
                    <Button variant="outline" onClick={() => { setOpen(false); setTimeout(() => run(), 1500); }} className="w-full rounded-full gap-1.5 border-violet-500/40 text-violet-300" data-testid="bot-rerun-btn">
                      <Bot className="w-4 h-4" /> Re-run robot to see the new score
                    </Button>
                  )}
                </div>
              ) : (
                <p className="text-sm text-emerald-400 font-semibold text-center py-2" data-testid="bot-clean">✨ Clean run — no problems found. Ship it!</p>
              )}

              {/* Shareable score card */}
              <div className="pt-1">
                {scoreCard ? (
                  <div className="space-y-2" data-testid="bot-scorecard">
                    <img src={scoreCard} alt="Robot score card" className="w-full rounded-xl border border-[hsl(var(--border))]" />
                    <div className="flex items-center gap-3">
                      <a href={scoreCard} download="gameview-robot-score.png" className="flex items-center gap-1.5 text-xs font-semibold text-violet-400 hover:underline" data-testid="bot-scorecard-download">
                        <Download className="w-3.5 h-3.5" /> Download
                      </a>
                      {canFix && (
                        posted ? (
                          <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-400" data-testid="bot-scorecard-posted">✓ Posted to feed</span>
                        ) : (
                          <button onClick={postToFeed} disabled={posting} className="flex items-center gap-1.5 text-xs font-semibold text-fuchsia-400 hover:underline disabled:opacity-60" data-testid="bot-scorecard-post-feed">
                            {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquarePlus className="w-3.5 h-3.5" />} Post to community feed
                          </button>
                        )
                      )}
                    </div>
                  </div>
                ) : (
                  <Button variant="outline" onClick={makeCard} disabled={cardLoading} className="w-full rounded-full gap-1.5 border-fuchsia-500/40 text-fuchsia-300" data-testid="bot-scorecard-btn">
                    {cardLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Make a shareable score card
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
